import { z } from 'zod';

let negativeCounter = -1;

export function getNextNegativeKey() {
  return negativeCounter--;
}

export const attributeTypeSchema = z.enum([
  'text',
  'function',
  'currency',
  'number',
  'boolean',
  'date',
]);

export enum IAttributeTypeEnum {
  text = 1,
  function = 6,
  currency = 5,
  number = 2,
  boolean = 4,
  date = 3,
}

export enum ICurrencyTypeEnum {
  EUR = 1,
  USD = 2,
  GBP = 3,
  CHF = 4,
}

export const attributeIdSchema = z.nativeEnum(IAttributeTypeEnum);

export const currencyIdSchema = z.nativeEnum(ICurrencyTypeEnum);

export const currencyOptionsSchema = z.enum(['EUR', 'USD', 'GBP', 'CHF']);

export const functionTypeSchema = z.enum([
  'Handelsregister Nachverfolgung',
  'GwG',
  'Gesellschafterliste Nachverfolgung',
  'Grundbuch Nachverfolgung',
]);

export const functionAttributeSchema = z.object({
  idMeasurementAttribute: z.number(),
  MeasurementAttributeType: z.literal(attributeTypeSchema.enum.function),
  idMeasurementAttributeType: z.literal(attributeIdSchema.enum.function),
  Name: functionTypeSchema,
  Required: z.boolean(),
  idSF: z.number(),

  // Not relevant in the template
  Value: z.number().optional(),
});

export const textAttributeSchema = z.object({
  idMeasurementAttribute: z.number(),
  Name: z.string().min(1).max(100),
  MeasurementAttributeType: z.literal(attributeTypeSchema.enum.text),
  idMeasurementAttributeType: z.literal(attributeIdSchema.enum.text),
  Required: z.boolean(),

  // Not relevant in the template
  Value: z.string().optional(),
});

export const numberAttributeSchema = z.object({
  idMeasurementAttribute: z.number(),
  Name: z.string().min(1).max(100),
  MeasurementAttributeType: z.literal(attributeTypeSchema.enum.number),
  idMeasurementAttributeType: z.literal(attributeIdSchema.enum.number),
  Required: z.boolean(),

  // Not relevant in the template
  Value: z.number().optional(),
});

export const monetaryValueAttributeSchema = z.object({
  idMeasurementAttribute: z.number(),
  Name: z.string().min(1).max(100),
  MeasurementAttributeType: z.literal(attributeTypeSchema.enum.currency),
  idMeasurementAttributeType: z.literal(attributeIdSchema.enum.currency),
  Required: z.boolean(),

  // Not relevant in the template
  CurrencyType: currencyOptionsSchema.optional(),
  idCurrencyType: currencyIdSchema.optional(),
  Value: z.number().optional(),
});

export const booleanAttributeSchema = z.object({
  idMeasurementAttribute: z.number(),
  Name: z.string().min(1).max(100),
  MeasurementAttributeType: z.literal(attributeTypeSchema.enum.boolean),
  idMeasurementAttributeType: z.literal(attributeIdSchema.enum.boolean),
  Required: z.boolean(),

  // Not relevant in the template
  Value: z.boolean().optional(),
});

export const dateAttributeSchema = z.object({
  idMeasurementAttribute: z.number(),
  Name: z.string().min(1).max(100),
  MeasurementAttributeType: z.literal(attributeTypeSchema.enum.date),
  idMeasurementAttributeType: z.literal(attributeIdSchema.enum.date),
  Required: z.boolean(),

  // Not relevant in the template
  Value: z.string().datetime().optional(),
});

export const attributeSchema = z.discriminatedUnion(
  'idMeasurementAttributeType',
  [
    textAttributeSchema,
    functionAttributeSchema,
    monetaryValueAttributeSchema,
    numberAttributeSchema,
    booleanAttributeSchema,
    dateAttributeSchema,
  ]
);

export enum IAutomationExecutionType {
  legal_requirement = 1,
  legal_transaction_state = 2,
  legal_transaction_progress = 3,
}

export const automationExecutionTypeIdSchema = z.nativeEnum(
  IAutomationExecutionType
);

export const automationTypeSchema = z.enum([
  'legal-requirement',
  'legal-transaction-state',
  'legal-transaction-progress',
]);

const automationBaseSchema = z.object({
  idAutomation: z.number(),

  // Used to determine if the automation has been executed
  executedAt: z.string().datetime().optional(),
});

export const legalRequirementAutomationSchema = automationBaseSchema.extend({
  AutomationExecutionType: z.literal(
    automationTypeSchema.enum['legal-requirement']
  ),
  idAutomationExecutionType: z.literal(
    automationExecutionTypeIdSchema.enum.legal_requirement
  ),
  idLegalRequirement: z.number().optional(),
  idLegalRequirementTemplate: z.number().optional(),
  idLegalRequirementState: z.number().min(1),
});

export const legalTransactionStateAutomationSchema =
  automationBaseSchema.extend({
    AutomationExecutionType: z.literal(
      automationTypeSchema.enum['legal-transaction-state']
    ),
    idAutomationExecutionType: z.literal(
      automationExecutionTypeIdSchema.enum.legal_transaction_state
    ),

    idLegalTransactionState: z.number().min(1),
  });

export const legalTransactionProgressAutomationSchema =
  automationBaseSchema.extend({
    AutomationExecutionType: z.literal(
      automationTypeSchema.enum['legal-transaction-progress']
    ),
    idAutomationExecutionType: z.literal(
      automationExecutionTypeIdSchema.enum.legal_transaction_progress
    ),
    idLegalTransactionProgress: z.number().min(1),
  });

export const automationSchema = z.discriminatedUnion(
  'idAutomationExecutionType',
  [
    legalRequirementAutomationSchema,
    legalTransactionStateAutomationSchema,
    legalTransactionProgressAutomationSchema,
  ]
);

export const measureSchema = z.object({
  idMeasurement: z.number(),
  Name: z.string().min(1).max(100),
  attributes: z.array(attributeSchema),

  // Not relevant in the template
  Completed: z.boolean(),
  automations: z.array(automationSchema).optional(),
});

export type ILegalRequirementAutomation = z.infer<
  typeof legalRequirementAutomationSchema
>;
export type ILegalTransactionStateAutomation = z.infer<
  typeof legalTransactionStateAutomationSchema
>;
export type ILegalTransactionProgressAutomation = z.infer<
  typeof legalTransactionProgressAutomationSchema
>;

export const automationTypeOptions = automationTypeSchema.options;

export const automationExecutionTypeOptions = () => {
  const values = Object.values(IAutomationExecutionType).filter(
    value => typeof value === 'number'
  ) as number[];
  return values;
};

export const automationTypeOptionsMap: Record<number, string> = {
  1: 'Anforderung',
  2: 'Vorgangsphase',
  3: 'Vorgangsstatus',
};

export type IAutomationType = z.infer<typeof automationTypeSchema>;

export const attributeTypeOptions = attributeTypeSchema.options;

export const attributeTypeIdOptions = () => {
  const values = Object.values(IAttributeTypeEnum).filter(
    value => typeof value === 'number'
  ) as number[];
  return values;
};

export type IAttributeType = z.infer<typeof attributeTypeSchema>;

export type IAttributeTypeId = z.infer<typeof attributeIdSchema>;

export const currencyOptions = currencyOptionsSchema.options;

export type ICurrency = z.infer<typeof currencyOptionsSchema>;

export type ICurrencyId = z.infer<typeof currencyIdSchema>;

export const functionTypeOptions = functionTypeSchema.options;

export type IFunctionType = z.infer<typeof functionTypeSchema>;

export const functionTypeOptionsMap: Record<number, string> = {
  1: 'Handelsregister Nachverfolgung',
  2: 'GwG',
  3: 'Gesellschafterliste Nachverfolgung',
  4: 'Grundbuch Nachverfolgung',
};

export const attributeTypeOptionsMap: Record<number, string> = {
  1: 'Text',
  2: 'Nummer',
  3: 'Datum',
  4: 'Checkbox',
  5: 'Währung',
  6: 'Funktion',
};

export type IAutomation = z.infer<typeof automationSchema>;

export type IFunctionAttribute = z.infer<typeof functionAttributeSchema>;
export type ITextAttribute = z.infer<typeof textAttributeSchema>;
export type IMonetaryValueAttribute = z.infer<
  typeof monetaryValueAttributeSchema
>;
export type INumberAttribute = z.infer<typeof numberAttributeSchema>;
export type IBooleanAttribute = z.infer<typeof booleanAttributeSchema>;
export type IDateAttribute = z.infer<typeof dateAttributeSchema>;
export type IAttribute = z.infer<typeof attributeSchema>;
export type IMeasure = z.infer<typeof measureSchema>;

export const isFunctionAttribute = (
  attribute: IAttribute
): attribute is IFunctionAttribute => {
  return attribute.idMeasurementAttributeType === IAttributeTypeEnum.function;
};

export const isMonetaryValueAttribute = (
  attribute: IAttribute
): attribute is IMonetaryValueAttribute => {
  return attribute.idMeasurementAttributeType === IAttributeTypeEnum.currency;
};

export const isTextAttribute = (
  attribute: IAttribute
): attribute is ITextAttribute => {
  return attribute.idMeasurementAttributeType === IAttributeTypeEnum.text;
};

export const isNumberAttribute = (
  attribute: IAttribute
): attribute is INumberAttribute => {
  return attribute.idMeasurementAttributeType === IAttributeTypeEnum.number;
};

export const isBooleanAttribute = (
  attribute: IAttribute
): attribute is IBooleanAttribute => {
  return attribute.idMeasurementAttributeType === IAttributeTypeEnum.boolean;
};

export const isDateAttribute = (
  attribute: IAttribute
): attribute is IDateAttribute => {
  return attribute.idMeasurementAttributeType === IAttributeTypeEnum.date;
};

export const isProgressAutomation = (
  automation: IAutomation
): automation is ILegalTransactionProgressAutomation => {
  return (
    automation.idAutomationExecutionType ===
    IAutomationExecutionType.legal_transaction_progress
  );
};

export const isStateAutomation = (
  automation: IAutomation
): automation is ILegalTransactionStateAutomation => {
  return (
    automation.idAutomationExecutionType ===
    IAutomationExecutionType.legal_transaction_state
  );
};

export const isRequirementAutomation = (
  automation: IAutomation
): automation is ILegalRequirementAutomation => {
  return (
    automation.idAutomationExecutionType ===
    IAutomationExecutionType.legal_requirement
  );
};

export const getInitialTextAttribute = (): ITextAttribute => {
  return {
    MeasurementAttributeType: 'text',
    idMeasurementAttribute: getNextNegativeKey(),
    idMeasurementAttributeType: IAttributeTypeEnum.text,
    Name: '',
    Required: false,
  };
};

export const getInitialFunctionAttribute = (): IFunctionAttribute => {
  return {
    idMeasurementAttributeType: IAttributeTypeEnum.function,
    MeasurementAttributeType: 'function',
    idMeasurementAttribute: getNextNegativeKey(),
    Name: functionTypeSchema.options[0],
    Required: false,
    idSF: 1,
  };
};

export const getInitialMonetaryValueAttribute = (): IMonetaryValueAttribute => {
  return {
    MeasurementAttributeType: 'currency',
    idMeasurementAttribute: getNextNegativeKey(),
    idMeasurementAttributeType: IAttributeTypeEnum.currency,
    Name: '',
    Required: false,
    CurrencyType: 'EUR',
    idCurrencyType: ICurrencyTypeEnum.EUR,
  };
};

export const getInitialNumberAttribute = (): INumberAttribute => {
  return {
    MeasurementAttributeType: 'number',
    idMeasurementAttribute: getNextNegativeKey(),
    idMeasurementAttributeType: IAttributeTypeEnum.number,
    Name: '',
    Required: false,
  };
};

export const getInitialBooleanAttribute = (): IBooleanAttribute => {
  return {
    MeasurementAttributeType: 'boolean',
    idMeasurementAttribute: getNextNegativeKey(),
    idMeasurementAttributeType: IAttributeTypeEnum.boolean,
    Name: '',
    Required: false,
  };
};

export const getInitialDateAttribute = (): IDateAttribute => {
  return {
    MeasurementAttributeType: 'date',
    idMeasurementAttribute: getNextNegativeKey(),
    idMeasurementAttributeType: IAttributeTypeEnum.date,
    Name: '',
    Required: false,
  };
};

export const getInitialMeasure = (): IMeasure => {
  return {
    idMeasurement: getNextNegativeKey(),
    Name: '',
    Completed: false,
    attributes: [],
  };
};

export const getInitialRequirementAutomation =
  (): ILegalRequirementAutomation => {
    return {
      idAutomation: getNextNegativeKey(),
      AutomationExecutionType: 'legal-requirement',
      idAutomationExecutionType: IAutomationExecutionType.legal_requirement,
      idLegalRequirementState: 0,
    };
  };

export const getInitialStateAutomation =
  (): ILegalTransactionStateAutomation => {
    return {
      idAutomation: getNextNegativeKey(),
      AutomationExecutionType: 'legal-transaction-state',
      idAutomationExecutionType:
        IAutomationExecutionType.legal_transaction_state,
      idLegalTransactionState: 0,
    };
  };

export const getInitialProgressAutomation =
  (): ILegalTransactionProgressAutomation => {
    return {
      idAutomation: getNextNegativeKey(),
      AutomationExecutionType: 'legal-transaction-progress',
      idAutomationExecutionType:
        IAutomationExecutionType.legal_transaction_progress,
      idLegalTransactionProgress: 0,
    };
  };
